"use client";

import { useEffect, useRef, useState, type ImgHTMLAttributes } from "react";

/**
 * When a below-the-fold image may start downloading.
 *  - "near": once it comes within NEAR_MARGIN of the viewport (scrolled to, or a horizontal rail
 *    scrolled to it — an overflow container clips the observation, so a rail's hidden items wait
 *    until the rail shows them).
 *  - "idle": as "near", or once the page has loaded and the main thread is idle, whichever comes
 *    first. For images a swipe reveals (gallery slides): they must be ready before the swipe, but
 *    not compete with the hero.
 *
 * `loading="lazy"` alone does not do this: Chrome's lazy-load margin is 1250-2500 px, so on a
 * phone it fetched the review wall and every gallery thumbnail during the hero's LCP window. A
 * deferred image renders without a source on the server and until its moment comes, so the HTML
 * itself triggers no request.
 */
export type ImageDefer = "near" | "idle";

const NEAR_MARGIN = "300px";

let observer: IntersectionObserver | null = null;
const pending = new Map<Element, () => void>();

function watch(element: Element, onNear: () => void): () => void {
  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        // A ratio, not isIntersecting: that is also true for a box that only touches the visible
        // area, which is where the next slide of a swipe track sits.
        if (entry.intersectionRatio <= 0) continue;
        const callback = pending.get(entry.target);
        pending.delete(entry.target);
        observer?.unobserve(entry.target);
        callback?.();
      }
    },
    { rootMargin: NEAR_MARGIN },
  );
  pending.set(element, onNear);
  observer.observe(element);
  return () => {
    pending.delete(element);
    observer?.unobserve(element);
  };
}

let idle: Promise<void> | null = null;

function loadedAndIdle(): Promise<void> {
  idle ??= new Promise((resolve) => {
    // Safari has no requestIdleCallback.
    const onIdle = () =>
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(() => resolve(), { timeout: 2000 })
        : window.setTimeout(resolve, 200);
    if (document.readyState === "complete") onIdle();
    else window.addEventListener("load", onIdle, { once: true });
  });
  return idle;
}

/** [ref for the <img>, whether its source may be set]. Without `defer` the source is set at once. */
export function useDeferredImage(defer: ImageDefer | undefined) {
  const ref = useRef<HTMLImageElement | null>(null);
  const [ready, setReady] = useState(!defer);
  useEffect(() => {
    if (ready || !ref.current) return;
    let live = true;
    const release = () => {
      if (live) setReady(true);
    };
    const unwatch = watch(ref.current, release);
    if (defer === "idle") void loadedAndIdle().then(release);
    return () => {
      live = false;
      unwatch();
    };
  }, [defer, ready]);
  return [ref, ready] as const;
}

// What a deferred <img> shows until its source is set: a transparent image, so the element stays a
// replaced box (an <img> with no src and an alt renders as inline alt text and loses its box). With
// width/height it carries their ratio, so a `h-auto` image keeps its height and nothing shifts.
export function placeholderSrc(width?: number | string, height?: number | string): string {
  const w = Number(width);
  const h = Number(height);
  if (w > 0 && h > 0) return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${w} ${h}'/%3E`;
  return "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
}

/** A plain <img> that waits for its moment (see ImageDefer); for images outside CdnImage. */
export function DeferredImg({ defer, src, srcSet, ...attrs }: ImgHTMLAttributes<HTMLImageElement> & { defer: ImageDefer }) {
  const [ref, ready] = useDeferredImage(defer);
  return (
    // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
    <img
      {...attrs}
      loading="lazy"
      ref={ref}
      src={ready ? src : placeholderSrc(attrs.width, attrs.height)}
      srcSet={ready ? srcSet : undefined}
    />
  );
}
